import type { ReactNode } from 'react'
import { cx } from '../components/ui'

/**
 * The play surface: always 4:3, always centred, letterboxed to fit whatever
 * viewport it lands in.
 *
 * Locking the aspect ratio is a fairness requirement, not a styling choice. The
 * server sends normalised coordinates; if one player's surface were tall and
 * another's wide, the same payload would spread the shapes differently and one
 * of them would have a materially easier scan.
 */
export function PlayField({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="flex min-h-0 w-full flex-1 items-center justify-center">
      <div
        className={cx(
          'relative aspect-[4/3] w-full max-h-full overflow-hidden rounded-2xl',
          'border border-white/[0.06] bg-ink-950/70',
          className,
        )}
        style={{
          // Never wider than the height allows, so the box shrinks rather than
          // overflowing on short landscape viewports.
          maxWidth: 'min(100%, calc((100dvh - 13rem) * 4 / 3))',
          boxShadow: 'inset 0 0 90px rgba(0,0,0,0.55)',
        }}
      >
        {/* A faint grid gives the eye anchor points for spatial counting without
            competing with the shapes themselves. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.055]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '12.5% 16.666%',
          }}
        />
        {children}
      </div>
    </div>
  )
}

/**
 * Positions one item using the server's normalised coordinates.
 *
 * `size` is a fraction of field *width*, which keeps shapes circular rather
 * than stretched on a surface that is wider than it is tall.
 *
 * Centring is done with negative margins, NOT `transform: translate(-50%,-50%)`.
 * These elements are animated by Motion, which owns the `transform` property
 * and overwrites whatever is set here with its own scale — which silently
 * turned centre-positioning into top-left positioning and pushed items off the
 * right and bottom edges by half their size.
 *
 * Percentage margins resolve against the containing block's *width* on both
 * axes, and the item is square in pixels, so -50% of `size` is exactly half the
 * item on both axes.
 */
export function fieldItemStyle(item: { x: number; y: number; size: number }) {
  return {
    left: `${item.x * 100}%`,
    top: `${item.y * 100}%`,
    width: `${item.size * 100}%`,
    aspectRatio: '1 / 1',
    marginLeft: `${item.size * -50}%`,
    marginTop: `${item.size * -50}%`,
  } as const
}
